import { z } from 'zod';

// Email regex pattern for stricter validation
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Session configuration
const SESSION_CONFIG = {
  MAX_DURATION_MS: 30 * 60 * 1000, // 30 minutes
  INACTIVITY_TIMEOUT_MS: 15 * 60 * 1000, // 15 minutes
};

// Rate limiting configuration
const RATE_LIMIT = {
  MAX_ATTEMPTS: 5,
  WINDOW_MS: 60000, // 1 minute
  BLOCK_DURATION_MS: 3600000, // 1 hour
};

// Enhanced form validation schema
export const formSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must not exceed 100 characters')
    .regex(/^[a-zA-Z\s-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  
  email: z.string()
    .email('Invalid email address')
    .regex(EMAIL_PATTERN, 'Please enter a valid email address')
    .toLowerCase(),
  
  weddingDate: z.string()
    .optional()
    .refine(date => !date || new Date(date) > new Date(), 'Wedding date must be in the future'),
  
  businessName: z.string()
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name must not exceed 100 characters')
    .regex(/^[a-zA-Z0-9\s-'&]+$/, 'Business name can only contain letters, numbers, spaces, hyphens, apostrophes, and ampersands')
    .optional(),
  
  serviceType: z.enum(['photographer', 'venue', 'catering', 'florist', 'music', 'planner', 'other'])
    .optional(),
});

// Security utility functions
export const security = {
  // Session management
  session: {
    start: (): void => {
      const sessionId = window.crypto.randomUUID();
      const session = {
        id: sessionId,
        startTime: Date.now(),
        lastActivity: Date.now(),
      };
      sessionStorage.setItem('userSession', JSON.stringify(session));
    },

    update: (): void => {
      const session = JSON.parse(sessionStorage.getItem('userSession') || '{}');
      if (session.id) {
        session.lastActivity = Date.now();
        sessionStorage.setItem('userSession', JSON.stringify(session));
      }
    },

    validate: (): boolean => {
      const session = JSON.parse(sessionStorage.getItem('userSession') || '{}');
      if (!session.id) return false;

      const now = Date.now();
      const sessionAge = now - session.startTime;
      const inactivityPeriod = now - session.lastActivity;

      if (sessionAge > SESSION_CONFIG.MAX_DURATION_MS || 
          inactivityPeriod > SESSION_CONFIG.INACTIVITY_TIMEOUT_MS) {
        sessionStorage.removeItem('userSession');
        return false;
      }

      security.session.update();
      return true;
    },

    end: (): void => {
      sessionStorage.removeItem('userSession');
    }
  },

  // Rate limiting
  isBlocked: (): boolean => {
    const blockUntil = Number(localStorage.getItem('blockUntil') || '0');
    return Date.now() < blockUntil;
  },

  checkRateLimit: (): boolean => {
    const attempts = JSON.parse(localStorage.getItem('attempts') || '[]');
    const now = Date.now();
    
    // Remove old attempts
    const recentAttempts = attempts.filter((timestamp: number) => 
      now - timestamp < RATE_LIMIT.WINDOW_MS
    );

    return recentAttempts.length < RATE_LIMIT.MAX_ATTEMPTS;
  },

  recordAttempt: (): void => {
    const attempts = JSON.parse(localStorage.getItem('attempts') || '[]');
    const now = Date.now();
    
    // Add new attempt
    attempts.push(now);
    
    // Remove old attempts
    const recentAttempts = attempts.filter((timestamp: number) => 
      now - timestamp < RATE_LIMIT.WINDOW_MS
    );
    
    // Check if should block
    if (recentAttempts.length >= RATE_LIMIT.MAX_ATTEMPTS) {
      localStorage.setItem('blockUntil', String(now + RATE_LIMIT.BLOCK_DURATION_MS));
    }
    
    localStorage.setItem('attempts', JSON.stringify(recentAttempts));
  },

  getRemainingCooldown: (): number => {
    const lastSubmission = Number(localStorage.getItem('lastFormSubmission') || '0');
    const cooldownEnd = lastSubmission + 30000; // 30 seconds cooldown
    const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
    return remaining;
  },

  // Device fingerprinting
  getDeviceFingerprint: (): string => {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency,
      navigator.deviceMemory,
      navigator.platform
    ];
    
    // Use SubtleCrypto for hashing
    const text = components.join('|');
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    
    return Array.from(data)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  },

  // Detect suspicious behavior
  detectSuspiciousBehavior: (): boolean => {
    const suspicious = [
      !navigator.cookieEnabled,
      !window.localStorage,
      !window.sessionStorage,
      /PhantomJS|Headless/.test(navigator.userAgent),
      navigator.webdriver,
      document.documentElement.getAttribute('webdriver'),
      navigator.languages?.length === 0,
      navigator.platform === ''
    ];
    return suspicious.some(Boolean);
  },

  // Clear all security data
  clearAllSecurityData: (): void => {
    localStorage.clear();
    sessionStorage.clear();
    security.session.end();
  }
};

// Export session configuration for use in components
export { SESSION_CONFIG, RATE_LIMIT };